import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

export interface SuggestionItem {
  id?: string | number;
  type?: string;
  value?: string;
  title?: string;
  display_text?: string;
}

export interface UseSuggestionsOptions {
  apiFunction: (query: string) => Promise<SuggestionItem[]>;
  testIdPrefix?: string;
  onSuggestionClick?: (suggestion: SuggestionItem) => void;
}

export interface UseSuggestionsReturn {
  // 状態
  suggestions: SuggestionItem[];
  isInputFocused: boolean;
  selectedSuggestionIndex: number;
  
  // ハンドラー
  handleFocus: () => void;
  handleBlur: (e: React.FocusEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleSuggestionKeyDown: (e: React.KeyboardEvent, index: number) => void;
  handleSuggestionClick: (suggestion: SuggestionItem) => void;
  
  // 状態更新
  setIsInputFocused: (focused: boolean) => void;
  updateQuery: (query: string) => void;
}

export const useSuggestions = ({
  apiFunction,
  testIdPrefix = 'suggestion',
  onSuggestionClick
}: UseSuggestionsOptions): UseSuggestionsReturn => {
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [currentQuery, setCurrentQuery] = useState('');
  const navigate = useNavigate();

  // サジェスト取得
  const fetchSuggestions = useCallback(async (query: string) => {
    const trimmed = query.trim();
    if (trimmed && isInputFocused) {
      try {
        const results = await apiFunction(trimmed);
        setSuggestions(results);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  }, [apiFunction, isInputFocused]);

  // クエリが変更されたときにサジェストを取得
  useEffect(() => {
    fetchSuggestions(currentQuery);
  }, [currentQuery, fetchSuggestions]);

  // クエリが変更されたときに選択インデックスをリセット
  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [currentQuery]);

  const handleFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && relatedTarget.getAttribute('data-testid')?.startsWith(`${testIdPrefix}-item-`)) {
      return;
    }
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (!activeElement || !activeElement.getAttribute('data-testid')?.startsWith(`${testIdPrefix}-item-`)) {
        setIsInputFocused(false);
      }
    }, 50);
  }, [testIdPrefix]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && suggestions.length > 0 && isInputFocused) {
      e.preventDefault();
      
      if (e.key === 'ArrowDown') {
        setSelectedSuggestionIndex(0);
        const firstSuggestionElement = document.querySelector(`[data-testid="${testIdPrefix}-item-0"]`);
        if (firstSuggestionElement instanceof HTMLElement) {
          firstSuggestionElement.focus();
        }
      } else if (e.key === 'ArrowUp') {
        const lastIndex = suggestions.length - 1;
        setSelectedSuggestionIndex(lastIndex);
        const lastSuggestionElement = document.querySelector(`[data-testid="${testIdPrefix}-item-${lastIndex}"]`);
        if (lastSuggestionElement instanceof HTMLElement) {
          lastSuggestionElement.focus();
        }
      }
    } else if (e.key === 'Escape' && isInputFocused) {
      e.preventDefault();
      setIsInputFocused(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [suggestions.length, isInputFocused, testIdPrefix]);

  const handleSuggestionKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (index + 1) % suggestions.length;
      setSelectedSuggestionIndex(nextIndex);
      const nextSuggestion = document.querySelector(`[data-testid="${testIdPrefix}-item-${nextIndex}"]`);
      if (nextSuggestion instanceof HTMLElement) {
        nextSuggestion.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        setSelectedSuggestionIndex(-1);
        const searchInput = document.querySelector(`[data-testid="${testIdPrefix}-input"]`);
        if (searchInput instanceof HTMLElement) {
          searchInput.focus();
        }
      } else {
        const prevIndex = index - 1;
        setSelectedSuggestionIndex(prevIndex);
        const prevSuggestion = document.querySelector(`[data-testid="${testIdPrefix}-item-${prevIndex}"]`);
        if (prevSuggestion instanceof HTMLElement) {
          prevSuggestion.focus();
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSuggestionClick(suggestions[index]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsInputFocused(false);
      setSelectedSuggestionIndex(-1);
      const searchInput = document.querySelector(`[data-testid="${testIdPrefix}-input"]`);
      if (searchInput instanceof HTMLElement) {
        searchInput.focus();
      }
    }
  }, [suggestions, testIdPrefix]);

  const handleSuggestionClick = useCallback((suggestion: SuggestionItem) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
    } else {
      // デフォルトの動作：検索ページに遷移
      const value = suggestion.value || suggestion.title || '';
      navigate(`/?q=${encodeURIComponent(value)}`);
    }
    setIsInputFocused(false);
  }, [onSuggestionClick, navigate]);

  const updateQuery = useCallback((query: string) => {
    setCurrentQuery(query);
  }, []);

  return {
    suggestions,
    isInputFocused,
    selectedSuggestionIndex,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleSuggestionKeyDown,
    handleSuggestionClick,
    setIsInputFocused,
    updateQuery
  };
}; 